// S2-LIB5: EmptyState
// Purpose: a neutral "nothing here yet / not enough data" placeholder used
// anywhere a panel would otherwise render a chart or list. Communicates
// context (title) + optional numeric detail (e.g. "2 / 5 calls today")
// without pretending there is data. Token-driven so it matches both the
// dashed "waiting-for-signal" look and the solid "empty-list" look.
import type { ReactNode } from 'react';
import './EmptyState.css';

// Public API — see docs/sprint-2/wireframes/s2-cc6-hero-and-section3.html
// for the canonical visual. `detail` is optional because many empty cases
// don't have a count to show (e.g. "No jobs yet").
export interface EmptyStateProps {
  icon?: ReactNode;                  // custom icon slot; if omitted, the default bar-chart SVG is used
  title: string;                     // required main message
  detail?: string;                   // optional secondary line (count/context, rendered in mono)
  variant?: 'dashed' | 'solid';      // default 'dashed'
}

// Default icon — inline SVG bar chart, 3 vertical bars of varying heights.
// Uses currentColor so the wrapper's `color` token drives the stroke, which
// also makes custom icons that use `currentColor` inherit the same tone.
function DefaultEmptyIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      width="100%"
      height="100%"
      aria-hidden="true"
    >
      <path d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 0 1 3 19.875v-6.75ZM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V8.625ZM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V4.125Z" />
    </svg>
  );
}

// EmptyState component.
// role="status" + aria-live="polite" ensures assistive tech announces the
// message when it appears (e.g. after a filter change leaves a panel empty).
export function EmptyState({
  icon,
  title,
  detail,
  variant = 'dashed',
}: EmptyStateProps) {
  return (
    <div
      className={`airgent-empty-state airgent-empty-state-${variant}`}
      role="status"
      aria-live="polite"
    >
      <div className="airgent-empty-state-icon">
        {icon ?? <DefaultEmptyIcon />}
      </div>
      <div className="airgent-empty-state-title">{title}</div>
      {detail && <div className="airgent-empty-state-detail">{detail}</div>}
    </div>
  );
}
